import React, { ReactNode, ErrorInfo } from 'react';

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  errorDetails: string | null;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { 
    hasError: false, 
    error: null,
    errorInfo: null,
    errorDetails: null
  };

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log the error to console
    console.error("React error boundary caught error:", error, errorInfo);
    
    // Try to extract more useful information
    let errorDetails = null;
    try {
      // Extract the component name from the stack
      const componentMatch = errorInfo.componentStack?.match(/\s+at\s+([A-Z][a-zA-Z0-9]+)/);
      const componentName = componentMatch ? componentMatch[1] : 'Unknown';
      
      // Extract the error location if possible
      const locationMatch = error.stack?.match(/at\s+([^(]+)\s*\((.+):(\d+):(\d+)\)/);
      const functionName = locationMatch ? locationMatch[1].trim() : 'Unknown function';
      const fileName = locationMatch ? locationMatch[2] : 'Unknown file';
      const lineNumber = locationMatch ? locationMatch[3] : 'Unknown line';
      
      errorDetails = `Error in component: ${componentName}\nFunction: ${functionName}\nFile: ${fileName}\nLine: ${lineNumber}\n\nPossible issue: Trying to access a property (likely 'length') of an undefined value`;
    } catch (e) {
      errorDetails = 'Failed to extract detailed error information';
    }
    
    this.setState({ errorInfo, errorDetails });
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div className="p-6 bg-red-50 border border-red-300 rounded-md max-w-3xl mx-auto my-8">
          <h2 className="text-red-600 text-2xl font-bold mb-4">Something went wrong</h2>
          
          <div className="bg-white p-4 rounded-md shadow-sm mb-4">
            <p className="text-red-500 font-semibold">{this.state.error?.message}</p>
          </div>
          
          {this.state.errorDetails && (
            <div className="bg-gray-800 text-white p-4 rounded-md mb-4 overflow-x-auto">
              <p className="font-bold text-yellow-400 mb-2">Error Details:</p>
              <pre className="whitespace-pre-wrap text-sm">
                {this.state.errorDetails}
              </pre>
            </div>
          )}
          
          <div className="bg-gray-100 p-4 rounded-md overflow-auto max-h-60 text-xs mb-4">
            <p className="font-bold mb-2">Component Stack:</p>
            <pre className="whitespace-pre-wrap">
              {this.state.errorInfo?.componentStack || "No component stack available"}
            </pre>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-4">
            <button 
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
              onClick={() => window.location.reload()}
            >
              Reload Page
            </button>
            
            <button 
              className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors"
              onClick={() => {
                // Log additional debug info to console
                console.log('Redux State:', (window as any).__REDUX_STATE__);
                console.log('React Components:', (window as any).__REACT_DEVTOOLS_GLOBAL_HOOK__?.renderers);
                alert('Additional debug info logged to console');
              }}
            >
              Log Debug Info
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary; 